import { useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { InputLabel, Poppins, ScenarioModalSkeleton, Spacer } from 'src/common';
import styled from 'styled-components';
import MaterialInput from '../../form/MaterialInput';
import { MAX_FORMULA_INT } from 'src/config';
import MaterialDatePicker from '../../form/MaterialDatePicker';
import { ConsequencesSelect, EventsSelect, ScenarioStatusSelect, SourcesSelect } from '../../select';
import { GenericSelect } from 'src/components/select/GenericSelect';
import { getCurrencySymbol } from 'src/utils/misc';
import Button from '../../form/Button';
import useVariables from 'src/hooks/useVariables';
import UserInput from 'src/components/UserInput';
import FormulaInput from 'src/components/FormulaInput';
import { CustomResultComponent } from 'src/components/FormulaInput/comps';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { DEFAULT_FORMULA_ERROR_MESSAGE, INVALID_SYNTAX_FORMULA_ERROR_MESSAGE } from 'src/components/FormulaInput/util';
import { VariablesWorkspaceModal } from 'src/components/modals/VariablesWorkspaceModal';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import { ConsequenceRangeIndicator, FrequencyIndicator } from './comps';
import { ScenarioInput, useUpsertWorkspaceVariablesMutation } from 'src/graphql/_generated';
import { ScenarioGroupSummary, useCache } from 'src/graphql';
import { useAuth } from 'src/state/auth';

const Div = styled.div`
  .grid {
    display: grid;
    grid-gap: 20px;

    &--2 {
      grid-template-columns: 1fr 1fr;
    }
    &--3 {
      grid-template-columns: 1fr 1fr 1fr;
    }
    &--auto {
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .grid {
      grid-gap: 10px;
    }
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    .grid {
      &--2 {
        grid-template-columns: 1fr;
      }
      &--3 {
        grid-template-columns: 1fr;
      }
      &--auto {
        grid-template-columns: 1fr;
      }
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
`;

const Frequency = styled.div`
  display: grid;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  grid-gap: 8px;
`;

const LabelBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 51px;
  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 40px;
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    height: 32px;
  }
`;

const NO_GROUP_VALUE = '';

interface ScenarioFormProps {
  buttonTitle: string;
  assessmentId?: string;
  assessmentName?: string;
  cb?: () => void;
  scenarioGroups?: ScenarioGroupSummary[];
  selectedGroupId?: string | null;
  onSelectGroup?: (id: string | null) => void;
  isLoadingScenarioGroups?: boolean;
  groupDirty?: boolean;
}

type VariablesModalState = null | { scope: 'workspace' } | { scope: 'assessment' };

const ScenarioForm: React.FC<ScenarioFormProps> = ({
  buttonTitle,
  assessmentId,
  assessmentName,
  cb,
  scenarioGroups = [],
  selectedGroupId,
  onSelectGroup,
  isLoadingScenarioGroups,
  groupDirty,
}) => {
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    setFieldValue,
    touched,
    setFieldTouched,
    handleSubmit,
    isValid,
    isSubmitting,
    initialValues,
    dirty,
  } = useFormikContext<ScenarioInput>();
  const [variablesModalState, setVariablesModalState] = useState<VariablesModalState>(null);
  const [lowerFormula, setLowerFormula] = useState('');
  const [upperFormula, setUpperFormula] = useState('');
  const [lowerEval, setLowerEval] = useState(0);
  const [upperEval, setUpperEval] = useState(0);
  const [lowerErr, setLowerErr] = useState({ mexp: '', other: '' });
  const [upperErr, setUpperErr] = useState({ mexp: '', other: '' });

  const { variables, containsUndefinedVariableFromFormula, invalidate } = useVariables({
    assessmentId: assessmentId as string,
    wvOverride: true,
  });

  const { profile } = useAuth();
  const { refetchQuery } = useCache();
  const [upsertWorkspaceVariables] = useUpsertWorkspaceVariablesMutation();

  const handleSaveVariableValue = useCallback(
    async ({ variableId, value, currency }: { variableId: number; value: number; currency: boolean }) => {
      const variable = variables?.find((v) => v.id === variableId);
      if (!variable || !profile?.activeWorkspace?.id) {
        return;
      }

      await upsertWorkspaceVariables({
        variables: {
          workspaceId: profile.activeWorkspace.id,
          variables: [
            {
              id: variable.id,
              name: variable.name,
              value: value,
              currency: currency,
              isGlobal: variable.isGlobal,
            },
          ],
        },
      });

      invalidate();
    },
    [variables, profile, upsertWorkspaceVariables, refetchQuery, invalidate],
  );

  useEffect(() => {
    if (lowerFormula) {
      if (lowerEval < 1) {
        setLowerErr((prev) => ({ ...prev, other: 'Minimum value is 1' }));
      } else if (lowerEval >= MAX_FORMULA_INT) {
        setLowerErr((prev) => ({ ...prev, other: 'Limit exceeded' }));
      } else {
        !lowerErr.mexp && setLowerErr({ mexp: '', other: '' });
      }
    } else {
      setLowerErr({ mexp: '', other: '' });
    }

    if (upperFormula) {
      if (Math.round(upperEval) <= Math.round(lowerEval)) {
        setUpperErr((prev) => ({
          ...prev,
          other: 'Must be greater than the Lower value (n.b. values are rounded to zero decimal places)',
        }));
      } else if (upperEval >= MAX_FORMULA_INT) {
        setUpperErr((prev) => ({ ...prev, other: 'Limit exceeded' }));
      } else {
        !upperErr.mexp && setUpperErr({ mexp: '', other: '' });
      }
    } else {
      setUpperErr({ mexp: '', other: '' });
    }
  }, [lowerFormula, upperFormula, lowerEval, upperEval]);

  const lowerContainsUndefinedVariable = containsUndefinedVariableFromFormula(lowerFormula);
  const upperContainsUndefinedVariable = containsUndefinedVariableFromFormula(upperFormula);

  const isFormulaSyntaxInvalid =
    lowerErr.mexp === INVALID_SYNTAX_FORMULA_ERROR_MESSAGE || upperErr.mexp === INVALID_SYNTAX_FORMULA_ERROR_MESSAGE;

  let lowerHelperText =
    !lowerContainsUndefinedVariable && touched.lowerFormula && (errors.lowerFormula || lowerErr.mexp || lowerErr.other);
  if (lowerHelperText === DEFAULT_FORMULA_ERROR_MESSAGE || lowerHelperText === INVALID_SYNTAX_FORMULA_ERROR_MESSAGE) {
    lowerHelperText = undefined;
  }

  let upperHelperText =
    !upperContainsUndefinedVariable && touched.upperFormula && (errors.upperFormula || upperErr.mexp || upperErr.other);
  if (upperHelperText === DEFAULT_FORMULA_ERROR_MESSAGE || upperHelperText === INVALID_SYNTAX_FORMULA_ERROR_MESSAGE) {
    upperHelperText = undefined;
  }

  const stripTypename = (obj: any) => {
    const { __typename, ...clean } = obj || {};
    return clean;
  };

  return (
    <>
      {variablesModalState && (
        <VariablesWorkspaceModal
          onClose={() => {
            invalidate();
            setVariablesModalState(null);
          }}
          scope={
            variablesModalState.scope === 'workspace'
              ? { type: 'workspace' }
              : { type: 'assessment', id: assessmentId || '', name: assessmentName || '' }
          }
          initHideEmptyVariables={false}
        />
      )}
      {!variables ? (
        <div className="h-padding">
          <ScenarioModalSkeleton />
        </div>
      ) : (
        <Div data-cy="scenario-form">
          <div className="h-padding">
            <div className="grid grid--3">
              <div>
                <InputLabel>STATUS</InputLabel>
                <ScenarioStatusSelect
                  value={values.status}
                  onChange={(o) => {
                    setFieldValue('status', o.value);
                    setTimeout(() => setFieldTouched('status', true));
                  }}
                  onBlur={handleBlur('status')}
                  error={touched.status && !!errors.status}
                  helperText={touched.status && errors.status}
                />
              </div>

              <div>
                <InputLabel>NAME *</InputLabel>
                <AutoGrowingInput
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  placeholder="Add a scenario"
                  error={!!errors.name}
                  helperText={errors.name}
                  css="width: 100%;"
                  data-cy="scenario-name-input"
                />
              </div>
              <div>
                <InputLabel>RISK OWNER</InputLabel>
                <UserInput
                  value={values.riskOwner || { text: '' }}
                  onChange={(usr) => {
                    setFieldValue('riskOwner', stripTypename(usr));
                  }}
                  onSelectUser={(usr) => {
                    setFieldValue('riskOwner', stripTypename(usr));
                  }}
                />
              </div>
            </div>
            <Spacer $px={20} />
            <div className={`grid ${scenarioGroups.length ? 'grid--auto' : 'grid--3'}`}>
              <div data-cy="scenario-sources-input">
                <InputLabel>SOURCES</InputLabel>
                <SourcesSelect
                  value={values.source}
                  onChange={(o) => {
                    setFieldValue('source', o.value);
                    setTimeout(() => setFieldTouched('source', true));
                  }}
                  onBlur={handleBlur('source')}
                  error={touched.source && !!errors.source}
                  helperText={touched.source && errors.source}
                />
              </div>
              <div data-cy="scenario-events-input">
                <InputLabel>EVENTS</InputLabel>
                <EventsSelect
                  value={values.event}
                  onChange={(o) => {
                    setFieldValue('event', o.value);
                    setTimeout(() => setFieldTouched('event', true));
                  }}
                  onBlur={handleBlur('event')}
                  error={touched.event && Boolean(errors.event)}
                  helperText={touched.event && errors.event}
                />
              </div>
              <div data-cy="scenario-consiquences-input">
                <InputLabel>CONSEQUENCES</InputLabel>
                <ConsequencesSelect
                  value={values.consequence}
                  onChange={(o) => {
                    setFieldValue('consequence', o.value);
                    setTimeout(() => setFieldTouched('consequence', true));
                  }}
                  onBlur={handleBlur('consequence')}
                  error={touched.consequence && Boolean(errors.consequence)}
                  helperText={touched.consequence && errors.consequence}
                />
              </div>
              {/* Scenario group selection temporarily disabled */}
              {/* {!!scenarioGroups.length && (
                <div>
                  <InputLabel>SCENARIO GROUP</InputLabel>
                  <GenericSelect
                    value={selectedGroupId ?? NO_GROUP_VALUE}
                    onChange={(opt) =>
                      onSelectGroup?.(
                        opt?.value === NO_GROUP_VALUE ? null : ((opt?.value as string | undefined) ?? null),
                      )
                    }
                    options={[{ label: 'No group', value: NO_GROUP_VALUE }, ...scenarioGroups.map((g) => ({ label: g.name, value: g.id }))]}
                    isLoading={isLoadingScenarioGroups}
                    placeholder="Select group"
                  />
                </div>
              )} */}
            </div>
          </div>
          <Spacer $px={20} />
          <Divider />
          <Spacer $px={20} />
          <div className="h-padding">
            <div className="grid grid--2">
              <div>
                <InputLabel>IDENTIFIED DATE</InputLabel>
                <MaterialDatePicker
                  value={values.riskIdentifiedDate || null}
                  onChange={(date) => setFieldValue('riskIdentifiedDate', date)}
                  inputProps={{
                    placeholder: 'Identified date',
                    onBlur: handleBlur('riskIdentifiedDate'),
                    error: touched.riskIdentifiedDate && Boolean(errors.riskIdentifiedDate),
                    helperText: touched.riskIdentifiedDate && errors.riskIdentifiedDate,
                    fullWidth: true,
                    ['data-cy']: 'identified-date-input',
                  }}
                />
              </div>
              <div>
                <InputLabel>REVIEW DATE</InputLabel>
                <MaterialDatePicker
                  value={values.reviewDate || null}
                  onChange={(date) => setFieldValue('reviewDate', date)}
                  inputProps={{
                    placeholder: 'Review date',
                    onBlur: handleBlur('reviewDate'),
                    error: touched.reviewDate && Boolean(errors.reviewDate),
                    helperText: touched.reviewDate && errors.reviewDate,
                    fullWidth: true,
                    ['data-cy']: 'review-date-input',
                  }}
                />
              </div>
            </div>
          </div>
          <Spacer $px={20} />
          <Divider />
          <Spacer $px={20} />
          <div className="h-padding">
            <InputLabel>RISK NARRATIVE</InputLabel>
            <div>
              <MaterialInput
                name="riskNarrative"
                value={values.riskNarrative}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter your text"
                css="width: 100%;"
                error={touched.riskNarrative && Boolean(errors.riskNarrative)}
                helperText={touched.riskNarrative && errors.riskNarrative}
                multiline
                minRows={3}
                maxRows={10}
                data-cy="scenario-narrative-input"
              />
            </div>
          </div>
          <Spacer $px={20} />
          <Divider />
          <Spacer $px={20} />
          <div className="h-padding">
            <Frequency>
              <div css="width: 100%;">
                <InputLabel css="display: block;">FREQUENCY</InputLabel>
                <MaterialInput
                  name="frequencyTimes"
                  type="number"
                  value={values.frequencyTimes || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFieldValue('frequencyTimes', value ? Number(value) : undefined);
                  }}
                  onBlur={handleBlur}
                  placeholder="X times"
                  error={touched.frequencyTimes && Boolean(errors.frequencyTimes)}
                  helperText={touched.frequencyTimes && errors.frequencyTimes}
                  data-cy="scenario-frequencty-times-input"
                  css="width: 100%;"
                />
              </div>
              <LabelBox>
                <InputLabel css="display: block;">TIMES EVERY</InputLabel>
              </LabelBox>
              <MaterialInput
                name="frequencyYears"
                type="number"
                value={values.frequencyYears || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFieldValue('frequencyYears', value ? Number(value) : undefined);
                }}
                onBlur={handleBlur}
                placeholder="Years"
                error={touched.frequencyYears && Boolean(errors.frequencyYears)}
                helperText={touched.frequencyYears && errors.frequencyYears}
                data-cy="scenario-frequency-years-input"
                css="width: 100%;"
              />
              <LabelBox>
                <InputLabel css="display: block;">YEAR(S)</InputLabel>
              </LabelBox>
            </Frequency>
            <Spacer $px={20} />
            <FrequencyIndicator times={values.frequencyTimes} years={values.frequencyYears} />
            <Spacer $px={20} />
            <InputLabel css="margin-right: 10px;">FREQUENCY - NOTE</InputLabel>
            <MaterialInput
              name="frequencyNote"
              value={values.frequencyNote}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={'Enter your text'}
              css="width: 100%;"
              error={touched.frequencyNote && Boolean(errors.frequencyNote)}
              helperText={touched.frequencyNote && errors.frequencyNote}
              data-cy="scenario-frequency-note-input"
              multiline
              minRows={3}
              maxRows={10}
            />
          </div>
          <Spacer $px={20} />
          <Divider />
          <Spacer $px={20} />
          <div className="h-padding">
            <div>
              <InputLabel>CONSEQUENCE - LOWER</InputLabel>
              <FormulaInput
                variables={variables}
                dataCy="scenario-lower-input"
                initaialExpression={initialValues.lowerFormula}
                onChange={(val) => {
                  setLowerFormula(val);
                  setFieldValue('lowerFormula', val);
                }}
                onEval={setLowerEval}
                helperText={lowerHelperText}
                error={!!(touched.lowerFormula && (errors.lowerFormula || lowerErr.mexp || lowerErr.other))}
                InputAdornmentStart={
                  <Poppins weight={500} px={18} color="cflowerBlue">
                    {getCurrencySymbol()}
                  </Poppins>
                }
                onErr={(err) => {
                  setLowerErr((prev) => ({ ...prev, mexp: err }));
                }}
                onBlur={() => setFieldTouched('lowerFormula', true)}
                onReset={
                  values.lowerFormula?.replace(/\s+/g, '') !== initialValues?.lowerFormula?.replace(/\s+/g, '')
                    ? () => setFieldValue('lowerFormula', initialValues?.lowerFormula)
                    : undefined
                }
                customResultComponent={
                  lowerContainsUndefinedVariable && (
                    <CustomResultComponent
                      onClick={() => {
                        setVariablesModalState({ scope: 'assessment' });
                        mpEvent(MPEvents.ButtonClick, {
                          button: 'here',
                          tags: ['VARIABLES'],
                        });
                      }}
                    />
                  )
                }
                onSaveVariableValue={handleSaveVariableValue}
                onAddWorkspaceVariable={() => setVariablesModalState({ scope: 'workspace' })}
                onAddAssessmentVariable={() => setVariablesModalState({ scope: 'assessment' })}
              />
            </div>
            <Spacer $px={20} />
            <div>
              <InputLabel>CONSEQUENCE - UPPER</InputLabel>
              <FormulaInput
                variables={variables}
                dataCy="scenario-upper-input"
                initaialExpression={initialValues.upperFormula}
                onChange={(val) => {
                  setUpperFormula(val);
                  setFieldValue('upperFormula', val);
                }}
                onEval={setUpperEval}
                helperText={upperHelperText}
                error={!!(touched.upperFormula && (errors.upperFormula || upperErr.mexp || upperErr.other))}
                InputAdornmentStart={
                  <Poppins weight={500} px={18} color="cflowerBlue">
                    {getCurrencySymbol()}
                  </Poppins>
                }
                onErr={(err) => {
                  setUpperErr((prev) => ({ ...prev, mexp: err }));
                  setFieldTouched('upperFormula', true);
                }}
                onBlur={() => setFieldTouched('upperFormula', true)}
                onReset={
                  values.upperFormula?.replace(/\s+/g, '') !== initialValues?.upperFormula?.replace(/\s+/g, '')
                    ? () => {
                        setFieldValue('upperFormula', initialValues?.upperFormula);
                      }
                    : undefined
                }
                customResultComponent={
                  upperContainsUndefinedVariable && (
                    <CustomResultComponent
                      onClick={() => {
                        setVariablesModalState({ scope: 'assessment' });
                        mpEvent(MPEvents.ButtonClick, {
                          button: 'here',
                          tags: ['VARIABLES'],
                        });
                      }}
                    />
                  )
                }
                onSaveVariableValue={handleSaveVariableValue}
                onAddWorkspaceVariable={() => setVariablesModalState({ scope: 'workspace' })}
                onAddAssessmentVariable={() => setVariablesModalState({ scope: 'assessment' })}
              />
            </div>
            <Spacer $px={20} />
            <ConsequenceRangeIndicator lowerResult={lowerEval} upperResult={upperEval} />
            <Spacer $px={20} />
            <InputLabel>CONSEQUENCE - NOTE</InputLabel>
            <MaterialInput
              name="consequenceNote"
              value={values.consequenceNote}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={'Enter your text'}
              css="width: 100%;"
              error={touched.consequenceNote && Boolean(errors.consequenceNote)}
              helperText={touched.consequenceNote && errors.consequenceNote}
              data-cy="scenario-consequence-note-input"
              multiline
              minRows={3}
              maxRows={10}
            />
          </div>
          <Spacer $px={40} />
          <div className="h-padding">
            <Button
              disabled={!isValid || isSubmitting || isFormulaSyntaxInvalid || !(dirty || groupDirty)}
              onClick={() => {
                handleSubmit();
                cb && cb();
              }}
              primary
              css="width: 100%;"
              data-cy="scenario-create-button"
            >
              {buttonTitle}
            </Button>
          </div>
        </Div>
      )}
    </>
  );
};

export default ScenarioForm;
